import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format, parseISO, differenceInMinutes } from 'date-fns';

import noHarvestsPng from '@/assets/illustrations/empty/no-harvests.png';
import noHuntsPng from '@/assets/illustrations/empty/no-hunts.png';

import { useSeasonStore } from '@/stores/season-store';
import { useHuntStore } from '@/stores/hunt-store';
import { useWaypointStore } from '@/stores/waypoint-store';
import { getHarvestRecordsBySeason } from '@/services/harvest-service';
import { db } from '@/services/storage/db';
import { OperationalGuide } from '@/components/common/OperationalGuide';
import type { HuntSit, HarvestRecord } from '@/types';

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 flex flex-col gap-1">
      <p className="field-label text-body-xs">
        {label}
      </p>
      <p className="font-display text-display-sm text-[var(--color-text-display)]">
        {value}
      </p>
    </div>
  );
}

function formatHours(minutes: number): string {
  if (minutes === 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function harvestLabel(record: HarvestRecord): string {
  if (record.species === 'Whitetail Deer') {
    if (record.sex === 'buck') {
      return record.antlerPoints ? `${record.antlerPoints}-Point Buck` : 'Buck';
    }
    if (record.sex === 'doe') return 'Doe';
    return 'Deer';
  }
  const sexPart = record.sex === 'buck' ? 'Male ' : record.sex === 'doe' ? 'Female ' : '';
  return `${sexPart}${record.species}`.trim();
}

export function SeasonArchivePage() {
  const { id: seasonId } = useParams<{ id: string }>();
  const { seasons } = useSeasonStore();
  const { hunts, loadHuntsForSeason } = useHuntStore();
  const { loadWaypoints: loadLocations, waypoints: locations } = useWaypointStore();

  const [allSits, setAllSits] = useState<HuntSit[]>([]);
  const [harvests, setHarvests] = useState<HarvestRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const season = seasonId ? seasons.find((s) => s.id === seasonId) : undefined;

  // Load all data for this season
  useEffect(() => {
    if (!seasonId) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- loading flag set before async fetch; reset in cleanup
    setIsLoading(true);
    void loadHuntsForSeason(seasonId);
  }, [seasonId, loadHuntsForSeason]);

  // Once hunts are loaded, load sits + harvests
  useEffect(() => {
    const seasonHunts = hunts.filter((h) => h.seasonId === seasonId);
    if (seasonHunts.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- early-return branch clears state before async path
      setAllSits([]);
      setHarvests([]);
      setIsLoading(false);
      return;
    }

    void (async () => {
      const [sitsArrays, harvestRecords] = await Promise.all([
        Promise.all(seasonHunts.map((h) => db.getHuntSitsByHunt(h.id))),
        getHarvestRecordsBySeason(seasonHunts.map((h) => h.id)),
      ]);
      setAllSits(sitsArrays.flat());
      setHarvests(harvestRecords);
      setIsLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hunts.length, seasonId]);

  // Load locations for property (use first hunt's property)
  useEffect(() => {
    const seasonHunts = hunts.filter((h) => h.seasonId === seasonId);
    const propertyId = seasonHunts[0]?.propertyId;
    if (propertyId) void loadLocations(propertyId);
  }, [hunts, seasonId, loadLocations]);

  // Compute total minutes from sits with both timeIn and timeOut
  const totalMinutes = allSits.reduce((acc, sit) => {
    if (!sit.timeIn || !sit.timeOut) return acc;
    const [inH, inM] = sit.timeIn.split(':').map(Number);
    const [outH, outM] = sit.timeOut.split(':').map(Number);
    if (inH === undefined || inM === undefined || outH === undefined || outM === undefined) return acc;
    const inDate = new Date(0, 0, 0, inH, inM);
    const outDate = new Date(0, 0, 0, outH, outM);
    const diff = differenceInMinutes(outDate, inDate);
    return diff > 0 ? acc + diff : acc;
  }, 0);

  const hasTimes = allSits.some((s) => s.timeIn && s.timeOut);

  const seasonHunts = hunts.filter((h) => h.seasonId === seasonId);

  if (!season) {
    return (
      <div className="flex flex-col gap-4 py-8">
        <p className="text-[var(--color-text-muted)] text-body-sm">Season not found.</p>
        <Link to="/seasons">
          <button type="button" className="text-[var(--color-accent)] text-body-sm hover:underline">
            Back to Seasons
          </button>
        </Link>
      </div>
    );
  }

  const dateRange = season.closerDate
    ? `${format(parseISO(season.openerDate), 'MMM d')} – ${format(parseISO(season.closerDate), 'MMM d, yyyy')}`
    : `Opened ${format(parseISO(season.openerDate), 'MMM d, yyyy')}`;

  return (
    <div className="flex flex-col gap-6" data-testid="season-archive-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to={`/seasons/${seasonId ?? ''}`}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          aria-label="Back to season"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </Link>
        <div className="flex-1 min-w-0">
          <h1
            className="font-display text-display-sm text-[var(--color-text-display)] truncate"
            data-testid="archive-season-name"
          >
            {season.name}
          </h1>
          <p className="text-body-xs text-[var(--color-text-muted)] mt-0.5">{dateRange}</p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-[var(--color-elevated)] rounded-lg" />
          ))}
        </div>
      ) : (
        <>
          {/* Overview Stats */}
          <div className="grid grid-cols-2 gap-3" data-testid="archive-stats">
            <StatCard label="Total Hunts" value={seasonHunts.length} />
            <StatCard label="Total Sits" value={allSits.length} />
            <StatCard label="Total Hours" value={hasTimes ? formatHours(totalMinutes) : '—'} />
            <StatCard label="Harvests" value={harvests.length} />
          </div>

          {/* Harvests */}
          <div className="flex flex-col gap-3">
            <p className="field-label">
              Harvests
            </p>

            {harvests.length === 0 ? (
              <OperationalGuide
                illustration={noHarvestsPng}
                heading="No Trophies Yet"
                subtext="Your successful hunts will appear here"
                compact
                data-guide-id="season-archive-no-harvests"
              />
            ) : (
              <div className="flex flex-col gap-2" data-testid="harvest-list">
                {harvests.map((record) => {
                  const location = record.waypointId
                    ? locations.find((l) => l.id === record.waypointId)
                    : undefined;
                  const hunt = seasonHunts.find((h) => h.id === record.huntId);
                  return (
                    <div
                      key={record.id}
                      className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4"
                      data-testid={`harvest-row-${record.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <p className="text-body-md text-[var(--color-text-primary)] font-medium">
                            {harvestLabel(record)}
                          </p>
                          <p className="text-body-xs text-[var(--color-text-muted)]">
                            {format(parseISO(record.date), 'MMM d, yyyy')}
                            {location ? ` · ${location.name}` : ''}
                          </p>
                          {hunt && (
                            <p className="text-body-xs text-[var(--color-text-muted)]">{hunt.name}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Hunts Timeline */}
          <div className="flex flex-col gap-3">
            <p className="field-label">
              Hunt Timeline
            </p>

            {seasonHunts.length === 0 ? (
              <OperationalGuide
                illustration={noHuntsPng}
                heading="No Hunts Logged"
                subtext="Log a hunt to start recording your season story"
                ctaLabel="Start a Hunt"
                ctaLink="/hunt/new"
                compact
                data-guide-id="season-archive-no-hunts"
              />
            ) : (
              <div className="flex flex-col gap-2" data-testid="hunt-timeline">
                {[...seasonHunts]
                  .sort((a, b) => a.startDate.localeCompare(b.startDate))
                  .map((hunt) => {
                    const huntSits = allSits.filter((s) => s.huntId === hunt.id);
                    const huntHarvests = harvests.filter((r) => r.huntId === hunt.id);
                    return (
                      <Link
                        key={hunt.id}
                        to={`/hunt/${hunt.id}`}
                        className="block bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 hover:border-[var(--color-border-subtle)] transition-colors"
                        data-testid={`hunt-timeline-row-${hunt.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <p className="text-body-md text-[var(--color-text-primary)] font-medium truncate">
                              {hunt.name}
                            </p>
                            <p className="text-body-xs text-[var(--color-text-muted)]">
                              {format(parseISO(hunt.startDate), 'MMM d')}
                              {hunt.startDate !== hunt.endDate
                                ? ` – ${format(parseISO(hunt.endDate), 'MMM d, yyyy')}`
                                : `, ${format(parseISO(hunt.startDate), 'yyyy')}`}
                            </p>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-body-xs bg-[var(--color-elevated)] text-[var(--color-text-muted)] border border-[var(--color-border)]">
                              {huntSits.length} sits
                            </span>
                            {huntHarvests.length > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-body-xs bg-[var(--color-accent-subtle)] text-[var(--color-accent)] border border-[var(--color-accent)]/30">
                                {huntHarvests.length} harvest{huntHarvests.length !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Top Conditions — stub for M52 */}
          <div className="flex flex-col gap-2">
            <p className="field-label">
              Top Conditions
            </p>
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
              <p className="text-body-sm text-[var(--color-text-muted)] italic">
                Conditions insights coming soon — we&rsquo;re tracking your best hunting days
              </p>
              {/* TODO: M52 — pull best-scoring days from conditions quality scores */}
            </div>
          </div>

          {/* Year-Over-Year Hook — stub for M32 */}
          {(() => {
            // TODO: M32 — link to year-over-year comparison
            const prev = seasons.find(
              (s) =>
                s.id !== season.id &&
                s.weaponType === season.weaponType &&
                s.openerDate < season.openerDate,
            );
            return prev ? (
              <div
                className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 flex flex-col gap-2"
                data-testid="yoy-hook"
              >
                <p className="field-label">
                  Year Over Year
                </p>
                <p className="text-body-sm text-[var(--color-text-secondary)]">
                  Compare this season to <strong>{prev.name}</strong>
                </p>
                <p className="text-body-xs text-[var(--color-text-muted)] italic">
                  Detailed comparison coming in a future update
                </p>
              </div>
            ) : null;
          })()}
        </>
      )}
    </div>
  );
}
